<template>
  <div class="space-y-5">
    <!-- COHORT -->
    <section>
      <h4 class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2 pb-1 border-b border-gray-100">Cohort</h4>
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="block text-[11px] font-medium text-gray-600 mb-1">Population</label>
          <select
            :value="local.population_da_id"
            @change="onField('population_da_id', $event.target.value ? Number($event.target.value) : null)"
            class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white text-gray-800 focus:outline-none focus:border-gray-400"
          >
            <option :value="null">—</option>
            <option v-for="p in populations" :key="p.id" :value="p.id">{{ p.name }} · #{{ p.id }}</option>
          </select>
        </div>
        <div>
          <label class="block text-[11px] font-medium text-gray-600 mb-1">Entity type</label>
          <select
            :value="local.unit_entity_type || ''"
            @change="onField('unit_entity_type', $event.target.value)"
            class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white text-gray-800 focus:outline-none focus:border-gray-400"
          >
            <option value="">—</option>
            <option v-for="et in entityTypes" :key="et" :value="et">{{ et }}</option>
          </select>
        </div>
      </div>
    </section>

    <!-- INTERVENTIONS -->
    <section>
      <div class="flex items-baseline gap-2 mb-2 pb-1 border-b border-gray-100">
        <h4 class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 flex-1">Interventions</h4>
        <button @click="addIntervention" class="text-[11px] text-blue-600 hover:text-blue-800 font-medium">+ Add</button>
      </div>

      <div v-if="!(local.interventions || []).length" class="text-[11px] text-gray-400 italic py-2">
        No interventions yet — add at least one.
      </div>

      <div v-for="(intv, idx) in (local.interventions || [])" :key="idx" class="border border-gray-200 rounded p-3 mb-2 bg-gray-50/50">
        <div class="flex gap-2 items-start mb-2">
          <!-- DA Picker -->
          <div class="flex-1 relative" :ref="el => { if (el) dropdownRefs[idx] = el }">
            <button
              @click="toggleDADropdown(idx)"
              type="button"
              class="w-full text-left px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-400 flex items-center gap-2"
            >
              <template v-if="intv.da_id && daLookup[intv.da_id]">
                <span class="text-[10px] font-mono text-gray-400 shrink-0">#{{ intv.da_id }}</span>
                <span class="text-xs text-gray-800 truncate">{{ daLookup[intv.da_id].name }}</span>
                <span class="text-[9px] px-1 py-0.5 rounded shrink-0" :class="roleClass(daLookup[intv.da_id].role)">{{ daLookup[intv.da_id].role || '—' }}</span>
              </template>
              <span v-else class="text-xs text-gray-400">Select intervention DA…</span>
            </button>

            <div v-if="openDropdown === idx" class="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-64 overflow-y-auto">
              <input
                v-model="daSearch"
                type="text"
                placeholder="Search by name, ID, or role…"
                class="w-full px-2 py-1.5 text-xs border-b border-gray-200 focus:outline-none"
              />
              <div
                v-for="da in filteredDAs"
                :key="da.id"
                @click="selectDA(idx, da.id)"
                class="px-2 py-1.5 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
              >
                <span class="text-[10px] font-mono text-gray-400 w-8 shrink-0">#{{ da.id }}</span>
                <span class="text-xs text-gray-800 truncate flex-1">{{ da.name }}</span>
                <span class="text-[9px] px-1 py-0.5 rounded shrink-0" :class="roleClass(da.role)">{{ da.role || '—' }}</span>
              </div>
              <div v-if="!filteredDAs.length" class="px-2 py-3 text-xs text-gray-400 text-center">No matches</div>
            </div>
          </div>

          <input
            :value="intv.alias || ''"
            @input="updateIntervention(idx, 'alias', $event.target.value)"
            type="text"
            placeholder="alias"
            class="w-32 text-xs px-2 py-1.5 border border-gray-200 rounded font-mono focus:outline-none focus:border-gray-400"
          />

          <button
            @click="removeIntervention(idx)"
            type="button"
            class="w-6 h-6 mt-0.5 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
          >×</button>
        </div>

        <textarea
          :value="intv.description || ''"
          @input="updateIntervention(idx, 'description', $event.target.value)"
          rows="2"
          placeholder="Description (shown to the LLM in the table catalog)"
          class="w-full text-xs px-2 py-1 border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-400 resize-none mb-1"
        />

        <button
          @click="toggleAdvanced(idx)"
          type="button"
          class="text-[10px] text-gray-400 hover:text-gray-600"
        >{{ advancedOpen[idx] ? '▾' : '▸' }} Advanced (join + params overrides)</button>

        <div v-if="advancedOpen[idx]" class="mt-2 pt-2 border-t border-dashed border-gray-200 space-y-2">
          <div>
            <label class="block text-[10px] font-medium text-gray-500 mb-1">Join</label>
            <textarea
              :value="joinStrs[idx] || ''"
              @input="updateJoinStr(idx, $event.target.value)"
              rows="3"
              spellcheck="false"
              :placeholder="`{ &quot;keys&quot;: [&quot;NPI&quot;, &quot;TIN&quot;] }  or  { &quot;on&quot;: &quot;BENE_MBI_ID = xwalk.PRIMARY_NPI&quot; }`"
              class="w-full text-[11px] px-1.5 py-1 border border-gray-200 rounded font-mono bg-white focus:outline-none focus:border-gray-400 resize-vertical"
            />
            <p v-if="joinErrors[idx]" class="text-[10px] text-red-500 mt-0.5">{{ joinErrors[idx] }}</p>
          </div>
          <div>
            <label class="block text-[10px] font-medium text-gray-500 mb-1">DA-SQL placeholder params</label>
            <textarea
              :value="paramsStrs[idx] || ''"
              @input="updateParamsStr(idx, $event.target.value)"
              rows="3"
              spellcheck="false"
              placeholder='{ "metric_id": "125" }'
              class="w-full text-[11px] px-1.5 py-1 border border-gray-200 rounded font-mono bg-white focus:outline-none focus:border-gray-400 resize-vertical"
            />
            <p v-if="paramsErrors[idx]" class="text-[10px] text-red-500 mt-0.5">{{ paramsErrors[idx] }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- TIME WINDOW -->
    <section>
      <h4 class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2 pb-1 border-b border-gray-100">Time Window (optional)</h4>
      <textarea
        v-model="timeWindowStr"
        @input="onTimeWindowChange"
        rows="3"
        spellcheck="false"
        placeholder='{ "type": "year", "year": 2025, "column": "PERFORMANCE_YEAR" }  — or  { "type": "relative_months", "months": 12, "column": "CLM_THRU_DT" }'
        class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded font-mono bg-white focus:outline-none focus:border-gray-400 resize-vertical"
      />
      <p v-if="timeWindowError" class="text-[10px] text-red-500 mt-0.5">{{ timeWindowError }}</p>
    </section>

    <!-- INLINE ROW CAP (only meaningful for inline_data delivery) -->
    <section v-if="deliveryMode === 'inline_data'">
      <h4 class="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2 pb-1 border-b border-gray-100">Inline Row Cap</h4>
      <div class="flex items-center gap-3">
        <input
          :value="local.inline_row_cap ?? 100"
          @input="onField('inline_row_cap', $event.target.value ? Number($event.target.value) : null)"
          type="number"
          min="1"
          class="w-32 text-xs px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-gray-400"
        />
        <span class="text-[11px] text-gray-500">Rows per intervention. Trades context for tokens. Default 100.</span>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DataAttributeSourceParams',

  props: {
    modelValue: { type: Object, default: () => ({}) },
    deliveryMode: { type: String, default: 'inline_data' },
  },
  emits: ['update:modelValue'],

  data() {
    return {
      local: this.normalize(this.modelValue),
      allDAs: [],
      entityTypes: [],
      openDropdown: null,
      daSearch: '',
      dropdownRefs: {},

      // String mirrors for JSON sub-fields (so the user can type in progress
      // without forcing valid JSON every keystroke).
      timeWindowStr: JSON.stringify(this.modelValue?.time_window || {}, null, 2),
      timeWindowError: null,

      joinStrs: this.initJsonStrs(this.modelValue?.interventions, 'join'),
      joinErrors: {},
      paramsStrs: this.initJsonStrs(this.modelValue?.interventions, 'params'),
      paramsErrors: {},
      advancedOpen: {},
    }
  },

  computed: {
    populations() {
      return this.allDAs
        .filter(d => d.role === 'population')
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    daLookup() {
      const m = {}
      this.allDAs.forEach(da => { m[da.id] = da })
      return m
    },
    filteredDAs() {
      if (!this.daSearch) return this.allDAs
      const q = this.daSearch.toLowerCase()
      return this.allDAs.filter(da =>
        (da.name || '').toLowerCase().includes(q) ||
        String(da.id).includes(q) ||
        (da.role || '').toLowerCase().includes(q),
      )
    },
  },

  watch: {
    modelValue: {
      deep: true,
      handler(val) {
        // Re-hydrate if the parent passes a different params object (e.g. on edit).
        if (JSON.stringify(val) !== JSON.stringify(this.local)) {
          this.local = this.normalize(val)
          this.timeWindowStr = JSON.stringify(val?.time_window || {}, null, 2)
          this.joinStrs = this.initJsonStrs(val?.interventions, 'join')
          this.paramsStrs = this.initJsonStrs(val?.interventions, 'params')
        }
      },
    },
  },

  mounted() {
    this.fetchData()
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    normalize(v) {
      const obj = JSON.parse(JSON.stringify(v || {}))
      if (!Array.isArray(obj.interventions)) obj.interventions = []
      return obj
    },

    initJsonStrs(interventions, key) {
      const out = {}
      ;(interventions || []).forEach((intv, i) => {
        out[i] = intv?.[key] ? JSON.stringify(intv[key], null, 2) : ''
      })
      return out
    },

    async fetchData() {
      try {
        const [daRes, etRes] = await Promise.all([
          axios.get('api/impact_analytics/attributes/'),
          axios.get('api/entity-types/'),
        ])
        this.allDAs = (daRes.data.results || daRes.data).slice().sort((a, b) => a.name.localeCompare(b.name))
        const etData = etRes.data.results || etRes.data
        this.entityTypes = [...new Set(etData.map(e => e.type_name))].sort()
      } catch (e) {
        console.error('DataAttributeSourceParams fetch failed:', e)
      }
    },

    emitUpdate() {
      this.$emit('update:modelValue', JSON.parse(JSON.stringify(this.local)))
    },

    onField(key, val) {
      this.local[key] = val
      this.emitUpdate()
    },

    addIntervention() {
      this.local.interventions.push({
        da_id: null,
        alias: '',
        description: '',
      })
      const idx = this.local.interventions.length - 1
      this.joinStrs[idx] = ''
      this.paramsStrs[idx] = ''
      this.advancedOpen[idx] = false
      this.emitUpdate()
    },

    removeIntervention(idx) {
      this.local.interventions.splice(idx, 1)
      // Re-key the per-row dicts to match the new indexes.
      this.joinStrs = this.shiftAfter(this.joinStrs, idx)
      this.paramsStrs = this.shiftAfter(this.paramsStrs, idx)
      this.joinErrors = this.shiftAfter(this.joinErrors, idx)
      this.paramsErrors = this.shiftAfter(this.paramsErrors, idx)
      this.advancedOpen = this.shiftAfter(this.advancedOpen, idx)
      this.emitUpdate()
    },

    shiftAfter(obj, removedIdx) {
      const next = {}
      for (const [k, v] of Object.entries(obj)) {
        const i = Number(k)
        if (i < removedIdx) next[i] = v
        else if (i > removedIdx) next[i - 1] = v
      }
      return next
    },

    updateIntervention(idx, key, val) {
      this.local.interventions[idx][key] = val
      this.emitUpdate()
    },

    updateJoinStr(idx, val) {
      this.joinStrs[idx] = val
      this.joinErrors[idx] = null
      if (!val.trim()) {
        delete this.local.interventions[idx].join
        this.emitUpdate()
        return
      }
      try {
        this.local.interventions[idx].join = JSON.parse(val)
        this.emitUpdate()
      } catch {
        this.joinErrors[idx] = 'Invalid JSON'
      }
    },

    updateParamsStr(idx, val) {
      this.paramsStrs[idx] = val
      this.paramsErrors[idx] = null
      if (!val.trim()) {
        delete this.local.interventions[idx].params
        this.emitUpdate()
        return
      }
      try {
        this.local.interventions[idx].params = JSON.parse(val)
        this.emitUpdate()
      } catch {
        this.paramsErrors[idx] = 'Invalid JSON'
      }
    },

    onTimeWindowChange() {
      this.timeWindowError = null
      if (!this.timeWindowStr.trim() || this.timeWindowStr.trim() === '{}') {
        delete this.local.time_window
        this.emitUpdate()
        return
      }
      try {
        this.local.time_window = JSON.parse(this.timeWindowStr)
        this.emitUpdate()
      } catch {
        this.timeWindowError = 'Invalid JSON'
      }
    },

    toggleAdvanced(idx) {
      this.advancedOpen[idx] = !this.advancedOpen[idx]
    },

    toggleDADropdown(idx) {
      this.openDropdown = this.openDropdown === idx ? null : idx
      if (this.openDropdown !== null) this.daSearch = ''
    },

    selectDA(idx, daId) {
      this.local.interventions[idx].da_id = daId
      // Helpful default: seed alias from DA name if empty.
      if (!this.local.interventions[idx].alias) {
        const da = this.daLookup[daId]
        if (da?.name) {
          this.local.interventions[idx].alias = da.name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
            .slice(0, 40)
        }
      }
      this.openDropdown = null
      this.emitUpdate()
    },

    roleClass(role) {
      const map = {
        population: 'bg-blue-50 text-blue-600',
        intervention: 'bg-purple-50 text-purple-600',
        denominator: 'bg-amber-50 text-amber-600',
        eagle_eye: 'bg-green-50 text-green-600',
        date_spine: 'bg-gray-100 text-gray-500',
        opportunity: 'bg-orange-50 text-orange-600',
      }
      return map[role] || 'bg-gray-100 text-gray-500'
    },

    handleClickOutside(e) {
      if (this.openDropdown !== null) {
        const ref = this.dropdownRefs[this.openDropdown]
        if (ref && !ref.contains(e.target)) this.openDropdown = null
      }
    },
  },
}
</script>
